import React, { useState, useEffect } from 'react';
import api from '../services/api';
import InputNombre from '../components/InputNombre';
import './Tresorerie.css';

interface Depense {
  id: number;
  libelle: string;
  categorie: string;
  montant: number;
  modePaiement: string;
  beneficiaire: string;
  boutiqueNom: string;
  statut: string;
  notes: string;
  createdAt: string;
}

interface StatsDepenses {
  total: number;
  totalMois: number;
  totalAnnee: number;
  parCategorie: Record<string, number>;
}

interface StatsVentes {
  nombreVentes: number;
  chiffreAffaires: number;
  parMode: {
    especes: number;
    mobileMoney: number;
    credit: number;
  };
}

const categories = [
  'loyer', 'salaires', 'electricite', 'transport',
  'communication', 'fournitures', 'maintenance',
  'impots', 'marketing', 'autre'
];

const categorieLabel = (cat: string) => {
  const labels: Record<string, string> = {
    loyer: '🏠 Loyer',
    salaires: '👥 Salaires',
    electricite: '⚡ Électricité',
    transport: '🚗 Transport',
    communication: '📱 Communication',
    fournitures: '📦 Fournitures',
    maintenance: '🔧 Maintenance',
    impots: '🏛️ Impôts & taxes',
    marketing: '📣 Marketing',
    autre: '📋 Autre',
  };
  return labels[cat] || cat;
};

const depenseVide = {
  libelle: '',
  categorie: 'autre',
  montant: 0,
  modePaiement: 'especes',
  beneficiaire: '',
  boutiqueNom: '',
  notes: '',
};

const fmt = (n: number) => Math.round(Number(n)).toLocaleString('fr-FR');

export default function Tresorerie() {
  const [onglet, setOnglet] = useState<'tresorerie' | 'depenses'>('tresorerie');
  const [depenses, setDepenses] = useState<Depense[]>([]);
  const [statsDepenses, setStatsDepenses] = useState<StatsDepenses | null>(null);
  const [statsVentes, setStatsVentes] = useState<StatsVentes | null>(null);
  const [chargement, setChargement] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [depenseSelectionnee, setDepenseSelectionnee] = useState<Depense | null>(null);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [form, setForm] = useState(depenseVide);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState('toutes');

  useEffect(() => {
    chargerDonnees();
  }, []);

  const chargerDonnees = async () => {
    try {
      const [depRes, statsDepRes, statsVentRes] = await Promise.all([
        api.get('/depenses'),
        api.get('/depenses/stats'),
        api.get('/ventes/stats/jour'),
      ]);
      setDepenses(depRes.data);
      setStatsDepenses(statsDepRes.data);
      setStatsVentes(statsVentRes.data);
    } catch (e) {
      setErreur('Impossible de charger les données');
    } finally {
      setChargement(false);
    }
  };

  const depensesFiltrees = depenses.filter(d =>
    filtreCategorie === 'toutes' || d.categorie === filtreCategorie
  );

  const ouvrirCreation = () => {
    setEnEdition(null);
    setForm(depenseVide);
    setErreur('');
    setShowModal(true);
  };

  const ouvrirEdition = (d: Depense) => {
    setEnEdition(d.id);
    setForm({ ...depenseVide, ...d });
    setErreur('');
    setShowModal(true);
  };

  const sauvegarder = async () => {
    if (!form.libelle || !form.montant) {
      setErreur('Le libellé et le montant sont obligatoires');
      return;
    }
    try {
      if (enEdition) {
        await api.put(`/depenses/${enEdition}`, form);
        setSucces('Dépense modifiée');
      } else {
        await api.post('/depenses', form);
        setSucces('Dépense enregistrée');
      }
      setShowModal(false);
      chargerDonnees();
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur');
    }
  };

  const supprimer = async () => {
    if (!depenseSelectionnee) return;
    try {
      await api.delete(`/depenses/${depenseSelectionnee.id}`);
      setSucces('Dépense supprimée');
      setShowDeleteModal(false);
      chargerDonnees();
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de la suppression');
    }
  };

  const exporterExcel = async () => {
    try {
      const res = await api.get('/depenses/export');
      const data = res.data;
      if (!data.length) return;
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(';'),
        ...data.map((row: any) => headers.map((h: string) => row[h]).join(';'))
      ].join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `depenses_nexacom_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      setSucces('Export téléchargé avec succès');
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de l\'export');
    }
  };

  if (chargement) return <div className="page-chargement">Chargement...</div>;

  return (
    <div className="tresorerie-page">
      <div className="tres-tabs">
        <button
          className={onglet === 'tresorerie' ? 'active' : ''}
          onClick={() => setOnglet('tresorerie')}>
          💰 Trésorerie
        </button>
        <button
          className={onglet === 'depenses' ? 'active' : ''}
          onClick={() => setOnglet('depenses')}>
          💸 Dépenses
        </button>
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      {/* ===== ONGLET TRÉSORERIE ===== */}
      {onglet === 'tresorerie' && (
        <div className="tres-content">
          <div className="page-header">
            <div>
              <h1>Trésorerie</h1>
              <p>Suivi des flux financiers et soldes</p>
            </div>
          </div>

          <div className="soldes-grid">
            <div className="solde-card especes">
              <div className="sc-icon">💵</div>
              <div className="sc-info">
                <div className="sc-titre">Encaissements Espèces</div>
                <div className="sc-montant">
                  {fmt(statsVentes?.parMode?.especes || 0)} FCFA
                </div>
                <div className="sc-sub">Aujourd'hui</div>
              </div>
            </div>
            <div className="solde-card mobile">
              <div className="sc-icon">📱</div>
              <div className="sc-info">
                <div className="sc-titre">Mobile Money</div>
                <div className="sc-montant">
                  {fmt(statsVentes?.parMode?.mobileMoney || 0)} FCFA
                </div>
                <div className="sc-sub">Orange + Moov + Wave</div>
              </div>
            </div>
            <div className="solde-card credit">
              <div className="sc-icon">💳</div>
              <div className="sc-info">
                <div className="sc-titre">Ventes à Crédit</div>
                <div className="sc-montant">
                  {fmt(statsVentes?.parMode?.credit || 0)} FCFA
                </div>
                <div className="sc-sub">En attente de règlement</div>
              </div>
            </div>
            <div className="solde-card total">
              <div className="sc-icon">📊</div>
              <div className="sc-info">
                <div className="sc-titre">CA Total du Jour</div>
                <div className="sc-montant">
                  {fmt(statsVentes?.chiffreAffaires || 0)} FCFA
                </div>
                <div className="sc-sub">{statsVentes?.nombreVentes || 0} ventes</div>
              </div>
            </div>
          </div>

          <div className="section-titre">Dépenses du mois en cours</div>
          <div className="depenses-resume">
            <div className="dr-total">
              <div className="dr-val">{fmt(statsDepenses?.totalMois || 0)} FCFA</div>
              <div className="dr-lbl">Total dépenses ce mois</div>
            </div>
            <div className="dr-categories">
              {statsDepenses && Object.entries(statsDepenses.parCategorie)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([cat, montant]) => (
                  <div key={cat} className="dr-cat-row">
                    <span>{categorieLabel(cat)}</span>
                    <div className="dr-bar-wrap">
                      <div className="dr-bar" style={{
                        width: `${Math.min(100, (montant / (statsDepenses.totalAnnee || 1)) * 100 * 3)}%`
                      }} />
                    </div>
                    <span className="dr-cat-montant">{fmt(montant)} F</span>
                  </div>
                ))}
              {(!statsDepenses || Object.keys(statsDepenses.parCategorie).length === 0) && (
                <div className="dr-empty">Aucune dépense enregistrée ce mois</div>
              )}
            </div>
          </div>

          <div className="export-section">
            <div className="export-info">
              <h3>📤 Export pour votre cabinet comptable</h3>
              <p>Téléchargez toutes vos dépenses au format CSV pour les transmettre à votre cabinet comptable.</p>
            </div>
            <button className="btn-export" onClick={exporterExcel}>
              ⬇️ Télécharger CSV
            </button>
          </div>
        </div>
      )}

      {/* ===== ONGLET DÉPENSES ===== */}
      {onglet === 'depenses' && (
        <div className="tres-content">
          <div className="page-header">
            <div>
              <h1>Dépenses</h1>
              <p>Enregistrez et suivez toutes vos charges</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-secondary" onClick={exporterExcel}>
                📤 Exporter CSV
              </button>
              <button className="btn-primary" onClick={ouvrirCreation}>
                + Nouvelle dépense
              </button>
            </div>
          </div>

          {statsDepenses && (
            <div className="stats-bar">
              <div className="stat-item">
                <span className="stat-val">{statsDepenses.total}</span>
                <span className="stat-lbl">Total dépenses</span>
              </div>
              <div className="stat-item">
                <span className="stat-val" style={{ color: '#A32D2D', fontSize: 16 }}>
                  {fmt(statsDepenses.totalMois)} F
                </span>
                <span className="stat-lbl">Ce mois</span>
              </div>
              <div className="stat-item">
                <span className="stat-val" style={{ color: '#854F0B', fontSize: 16 }}>
                  {fmt(statsDepenses.totalAnnee)} F
                </span>
                <span className="stat-lbl">Total cumulé</span>
              </div>
            </div>
          )}

          <div className="filtre-cats">
            <button
              className={filtreCategorie === 'toutes' ? 'active' : ''}
              onClick={() => setFiltreCategorie('toutes')}>
              Toutes
            </button>
            {categories.map(c => (
              <button key={c}
                className={filtreCategorie === c ? 'active' : ''}
                onClick={() => setFiltreCategorie(c)}>
                {categorieLabel(c).split(' ')[1] || c}
              </button>
            ))}
          </div>

          <div className="depenses-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Libellé</th>
                  <th>Catégorie</th>
                  <th>Bénéficiaire</th>
                  <th>Mode</th>
                  <th>Montant</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {depensesFiltrees.map(d => (
                  <tr key={d.id}>
                    <td className="dep-date">
                      {new Date(d.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td>
                      <div className="dep-libelle">{d.libelle}</div>
                      {d.notes && <div className="dep-notes">{d.notes}</div>}
                    </td>
                    <td>{categorieLabel(d.categorie)}</td>
                    <td>{d.beneficiaire || '—'}</td>
                    <td className="dep-mode">{d.modePaiement}</td>
                    <td className="dep-montant">{fmt(d.montant)} FCFA</td>
                    <td>
                      <span className={`dep-statut ${d.statut}`}>
                        {d.statut === 'validee' ? '✓ Validée' :
                          d.statut === 'en_attente' ? '⏳ En attente' : '✗ Rejetée'}
                      </span>
                    </td>
                    <td>
                      <div className="td-actions">
                        <button onClick={() => ouvrirEdition(d)}>✏️</button>
                        <button onClick={() => {
                          setDepenseSelectionnee(d);
                          setShowDeleteModal(true);
                        }}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {depensesFiltrees.length === 0 && (
              <div className="table-empty">Aucune dépense enregistrée</div>
            )}
          </div>
        </div>
      )}

      {/* MODAL CRÉATION / ÉDITION */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{enEdition ? 'Modifier la dépense' : 'Nouvelle dépense'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="form-group">
                <label>Libellé <span className="req">*</span></label>
                <input
                  value={form.libelle}
                  onChange={e => setForm(f => ({ ...f, libelle: e.target.value }))}
                  placeholder="Ex: Facture SONABEL avril"
                />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select
                    value={form.categorie}
                    onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}>
                    {categories.map(c => (
                      <option key={c} value={c}>{categorieLabel(c)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Montant (FCFA) <span className="req">*</span></label>
                  <InputNombre
                    value={form.montant}
                    onChange={val => setForm(f => ({ ...f, montant: val }))}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>Mode de paiement</label>
                  <select
                    value={form.modePaiement}
                    onChange={e => setForm(f => ({ ...f, modePaiement: e.target.value }))}>
                    <option value="especes">💵 Espèces</option>
                    <option value="orange_money">🟠 Orange Money</option>
                    <option value="moov_money">🟡 Moov Money</option>
                    <option value="wave">🔵 Wave</option>
                    <option value="virement">🏦 Virement bancaire</option>
                    <option value="cheque">📝 Chèque</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Bénéficiaire</label>
                  <input
                    value={form.beneficiaire}
                    onChange={e => setForm(f => ({ ...f, beneficiaire: e.target.value }))}
                    placeholder="Ex: SONABEL, Bailleur..."
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>Boutique concernée</label>
                  <input
                    value={form.boutiqueNom}
                    onChange={e => setForm(f => ({ ...f, boutiqueNom: e.target.value }))}
                    placeholder="Ex: Boutique Centrale"
                  />
                </div>
                <div className="form-group">
                  <label>Référence / N° pièce</label>
                  <input
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Ex: Facture N°123"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Annuler
              </button>
              <button className="btn-primary" onClick={sauvegarder}>
                {enEdition ? 'Enregistrer' : 'Créer la dépense'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION */}
      {showDeleteModal && depenseSelectionnee && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header danger">
              <h2>⚠️ Supprimer la dépense</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Voulez-vous supprimer <strong>{depenseSelectionnee.libelle}</strong> ?</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Annuler
              </button>
              <button className="btn-danger" onClick={supprimer}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}